/**
 * useReveal — React hook for scroll-triggered fade+slide-up reveals.
 *
 * Returns a ref + props you spread onto an element. The element
 * fades + slides up 22px when it enters the viewport. One-shot.
 * Self-contained: injects its own CSS once via a <style> tag so no
 * external CSS file is required.
 *
 * Usage:
 *   const reveal = useReveal();
 *   return <div ref={reveal.ref} {...reveal.props}>...</div>;
 *
 *   // Stagger via delay prop (1..5):
 *   const r2 = useReveal({ delay: 1 });
 *
 * Provenance: paperclip/context/pattern-picker/COMPETITOR_REVIEW_TRYMAKO_2026-05-13.md §9b
 */
import { useEffect, useRef, useState } from "react";

export type UseRevealOptions = {
  /** Stagger delay tier: 1=60ms, 2=120ms, ... 5=300ms. Default 0 (none). */
  delay?: 0 | 1 | 2 | 3 | 4 | 5;
  /** Rebind on element key change (rare). */
  resetKey?: string | number;
};

const REVEAL_CSS = `
html.js-ready [data-r] {
  opacity: 0;
  transform: translateY(22px);
  transition: opacity 0.55s ease, transform 0.55s ease;
}
html.js-ready [data-r].in {
  opacity: 1;
  transform: translateY(0);
}
html.js-ready [data-r][data-r-delay="1"] { transition-delay: 60ms; }
html.js-ready [data-r][data-r-delay="2"] { transition-delay: 120ms; }
html.js-ready [data-r][data-r-delay="3"] { transition-delay: 180ms; }
html.js-ready [data-r][data-r-delay="4"] { transition-delay: 240ms; }
html.js-ready [data-r][data-r-delay="5"] { transition-delay: 300ms; }
@media (prefers-reduced-motion: reduce) {
  html.js-ready [data-r] { opacity: 1; transform: none; transition: none; }
}
`;

let revealCSSInjected = false;
function injectRevealCSS() {
  if (revealCSSInjected || typeof document === "undefined") return;
  const style = document.createElement("style");
  style.textContent = REVEAL_CSS;
  document.head.appendChild(style);
  revealCSSInjected = true;
}

export function useReveal<T extends HTMLElement = HTMLDivElement>(
  options: UseRevealOptions = {}
) {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    injectRevealCSS();

    const el = ref.current;
    if (!el) return;

    // Make sure the global js-ready class is on the html element so the
    // companion CSS hides + transitions correctly.
    if (typeof document !== "undefined") {
      document.documentElement.classList.add("js-ready");
    }

    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      setVisible(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // Defer one frame so the transition is visible (not snap).
            requestAnimationFrame(() => setVisible(true));
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -6% 0px", threshold: 0.01 }
    );
    io.observe(el);

    // Safety net for above-fold elements that don't trigger IO on mount.
    const safety = window.setTimeout(() => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      if (rect.top < window.innerHeight) setVisible(true);
    }, 1200);

    return () => {
      io.disconnect();
      window.clearTimeout(safety);
    };
  }, [options.resetKey]);

  const dataR = "data-r" as const;
  const delayAttr =
    options.delay && options.delay > 0
      ? { "data-r-delay": String(options.delay) }
      : {};

  return {
    ref,
    visible,
    /** Spread these props onto the target element. */
    props: {
      [dataR]: "",
      ...delayAttr,
      className: visible ? "in" : "",
    } as {
      "data-r": "";
      "data-r-delay"?: string;
      className: string;
    },
  };
}
